import URI from 'urijs';
import domainsService from '@clubmed/domains-service';
import { appQueryParameters, oidc as oidcUtils } from '@clubmed/utils';
import config from 'Config';
import { QUICK_SEARCH_URL } from 'Routes/constants';
import { isProduction } from 'Utils/context/contextUtils';
import { ACCESS_TOKEN, USER_INFO, SELLER_INFO, SELLER_LOGGED } from 'Utils/ple-utils/cookies';
import { decryptHeptaSeller } from 'Utils/seller/sellerUtils';
type sellerInfoType = {
  agent?: string;
  agency?: string;
  agencyId?: string;
  clubmedId?: string;
  countryCode?: string;
  isDelCredere?: boolean;
  email: string;
  salesmanID?: string;
};
type userInfoType = {
  customerType?: string;
  customerStatus?: string;
  isLoggedIn?: boolean;
  isQuickLoggedIn?: boolean;
  neolaneId?: string;
};
export function initAppContext(
  uri: string,
  {
    cookies = {},
    featureFlippingKeysFromCms = null,
    locale = 'fr-FR',
    originalUrl,
    requestId,
    userAgent,
    device,
  }: Partial<AppContext> = {},
): AppContext {
  const [language, country] = domainsService.isValidLocale(locale) ? locale.split('-') : [];
  const uriObject = new URI(uri);
  const queryParameters = uriObject.query(true);
  const isComingFromMobileApp = !!queryParameters?.fromMobileApp || !!cookies.fromMobileApp;
  let encodedUserInfo: userInfoType | null | undefined = cookies[USER_INFO]
    ? oidcUtils.fromUriEncodedState(cookies[USER_INFO])
    : undefined;

  // handle the mobile App
  if (isComingFromMobileApp && !encodedUserInfo && queryParameters?.cm_userinfo) {
    encodedUserInfo = oidcUtils.fromUriEncodedState(queryParameters?.cm_userinfo) || undefined;
  }

  const sellerInfoCookies: sellerInfoType | null | undefined = cookies[SELLER_INFO]
    ? oidcUtils.fromUriEncodedState(cookies[SELLER_INFO])
    : undefined;
  const domainServiceForHostname = domainsService.forHostname(uriObject.hostname());
  const availableLocales = domainServiceForHostname.getAllLocales();
  const defaultLocale = domainServiceForHostname.getDefaultLocale();
  const {
    cmta: { cm_ta },
    cxo: { cxo_sales_network_id },
    indirect,
  } = appQueryParameters;
  const isCmta = queryParameters[cm_ta] !== undefined;
  const isIndirect = queryParameters[indirect] !== undefined;
  const isDirectCxo =
    (!!queryParameters[cxo_sales_network_id] && !isIndirect && !isCmta) ||
    !!queryParameters.cxo_neolid;
  const isIndirectCxo = !!queryParameters[cxo_sales_network_id] && isIndirect && !isCmta;
  const isCxo = (isDirectCxo || isIndirectCxo) && !isCmta;
  const localeCurrencies = availableLocales?.[locale]?.currencies || {};
  const isSeller = isCmta || isDirectCxo || isIndirectCxo;
  const currencyCode = Object.keys(localeCurrencies)[0]; // EUR

  return {
    freeDepositDeadline: 999,
    authenticationToken:
      cookies[ACCESS_TOKEN] ||
      cookies.CM_SELLER_TOKEN ||
      (isComingFromMobileApp ? queryParameters.CM_TOKEN : ''),
    availableLocales,
    bypassCache: queryParameters[appQueryParameters.bypassCache] === 'true',
    canUseOnlinePayment: domainsService.getEnabledPaymentProviders(locale).length > 0,
    cmsContent: queryParameters.commonContent || config.get('cms.commonContent'),
    cmsRelease: queryParameters.commonRelease || config.get('cms.commonRelease'),
    cmta: isCmta
      ? {
          agency: sellerInfoCookies?.agency || '',
          agent: sellerInfoCookies?.agent || '',
          isDelCredere: sellerInfoCookies?.isDelCredere || false,
          email: sellerInfoCookies?.email || '',
          searchType: uriObject.path() === QUICK_SEARCH_URL ? 'quick' : 'advanced',
          salesmanID: sellerInfoCookies?.salesmanID || '',
        }
      : {},
    cookies,
    country: country || '',
    countryCode: isSeller ? sellerInfoCookies?.countryCode || '' : availableLocales?.[locale]?.PAYS,
    countryName: availableLocales?.[locale]?.name,
    currency: currencyCode,
    customerNeolaneId: queryParameters.cxo_neolid,
    callReference: queryParameters.call_reference,
    contactID: queryParameters.contactID,
    customerType: encodedUserInfo?.customerType || '',
    customerStatus: encodedUserInfo?.customerStatus || '',
    cxoQueryParams: isCxo
      ? {
          salesman_code: queryParameters.salesman_code,
          clubmed_id: sellerInfoCookies?.clubmedId || '',
          agency_id: sellerInfoCookies?.agencyId || '',
          cxo_azur_code: queryParameters.cxo_azur_code || cookies.cxo_azur_code,
          cxo_sales_channel: queryParameters.cxo_sales_channel || cookies.cxo_sales_channel,
          cxo_country_code: queryParameters.cxo_country_code || cookies.cxo_country_code,
          cxo_user_id:
            typeof queryParameters.cxo_id === 'string'
              ? decryptHeptaSeller(queryParameters.cxo_id)
              : queryParameters.cxo_user_id || cookies.cxo_user_id || '',
          opportunity_id: queryParameters.cxo_opportunity_guid,
          sales_network_id: queryParameters.sales_network_id,
        }
      : {},
    defaultLocale,
    displayUnicodeFields: domainsService.shouldDisplayUnicodeFields(locale) || false,
    featureFlippingKeysFromCms,
    // TODO replace this by - and improve for SSR - GoogleAnalytics tracking helper
    googleTrackingId: queryParameters.visitorGUID || cookies._ga || null,
    headers: {},
    isProduction: isProduction(),
    isCmta,
    isCxo,
    isDirectCxo,
    isIndirectCxo,
    isSeller,
    isSellerLogged: cookies[SELLER_LOGGED] === 'true',
    isUserLogged: encodedUserInfo?.isLoggedIn || false,
    isUserQuickLogged: encodedUserInfo?.isQuickLoggedIn || false,
    isComingFromMobileApp,
    language: language || '',
    locale,
    neolaneId:
      cookies.cm_client_uid ||
      (encodedUserInfo && encodedUserInfo.neolaneId) ||
      queryParameters.cxo_neolId ||
      null,
    originalUrl,
    pagesContent: queryParameters.pagesContent || config.get('cms.pagesContent'),
    pagesRelease: queryParameters.pagesRelease || config.get('cms.pagesRelease'),
    requestId,
    userAgent,
    device,
  };
}
